import { useEffect, useRef } from 'react';
import Router from 'next/router';
import { useOrganizationData } from '@/contexts/OrganizationContext';
import { pushIntercomEvent, getDashboardUrl } from '@/utils/intercomDataLayer';
import { deriveOrganization } from '@/session/organization';

// Fires intercom_update on every route change EXCEPT during an in-flight
// org switch. The URL's sitename updates instantly on navigation, but
// OrganizationContext.organization lags by one API round-trip. If we
// fired intercom_update in that window, GTM Data Layer Variables for
// org_id / Acenda Organization would walk back through the dataLayer
// and pick up the previous intercom_identify (= the OLD org), producing
// a Frankenstein event with the new sitename and the old org identity.
// Skipping it lets the next intercom_identify deliver a consistent
// snapshot once OrganizationContext catches up.
export const IntercomRouteEffect = (): null => {
  const { organization } = useOrganizationData();
  const organizationRef = useRef(organization);

  useEffect(() => {
    organizationRef.current = organization;
  }, [organization]);

  useEffect(() => {
    const handleRouteChangeComplete = (url: string): void => {
      const urlSlug = deriveOrganization(Router.pathname, url);
      const org = organizationRef.current;
      if (!org || org.name !== urlSlug) return;
      pushIntercomEvent('intercom_update', {
        sitename: org.name,
        dashboard_url: getDashboardUrl(urlSlug),
        organization_id: String(org.id),
        organization_name: org.title || org.name,
      });
    };

    Router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      Router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, []);

  return null;
};
